"use client"

import { useState, useEffect, useRef } from "react"
import { Link, useNavigate } from "react-router-dom"
import { FaEye, FaEyeSlash } from "react-icons/fa"
import { UserAuth } from "../context/AuthContext"
import Form from "react-bootstrap/Form"
import Swal from "sweetalert2"
import { error4 } from "../constants/codeValidationErrors"
import Loading from "../components/Loading"
import { useTranslations } from "../translations"

const LoginPage = () => {
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [toggleEye, setToggleEye] = useState(false)

  // OTP (código por correo)
  const [mode, setMode] = useState("password") // "password" | "otp"
  const [otpStep, setOtpStep] = useState("email") // "email" | "code"
  const [otpEmail, setOtpEmail] = useState("")
  const [code, setCode] = useState(["", "", "", "", "", ""])
  const [sending, setSending] = useState(false)
  const [verifying, setVerifying] = useState(false)
  const [cooldown, setCooldown] = useState(0)
  const cellRefs = useRef([])

  const navigate = useNavigate()
  const { signIn, requestOtp, verifyOtp, user } = UserAuth()
  const { t, language } = useTranslations()
  const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 5000,
    timerProgressBar: true,
    didOpen: (toast) => {
      toast.addEventListener("mouseenter", Swal.stopTimer)
      toast.addEventListener("mouseleave", Swal.resumeTimer)
    },
  })

  useEffect(() => {
    if (user) {
      navigate("/dashboard")
    }
  }, [user])

  // Cuenta regresiva para reenviar el código.
  useEffect(() => {
    if (cooldown <= 0) return
    const id = setInterval(() => setCooldown((s) => (s <= 1 ? 0 : s - 1)), 1000)
    return () => clearInterval(id)
  }, [cooldown])

  const handleToggleEye = () => setToggleEye(!toggleEye)

  // Encamina según el rol devuelto por el contexto (padre, escuela, profesor, admin).
  const routeAfterAuth = (res) => {
    if (res && res.isAdmin) return window.location.replace("/admin/payments")
    if (res && res.role === "escuela") return window.location.replace("/school")
    if (res && res.role === "maestro") return window.location.replace("/teacher")
    if (res && res.role === "representante") return window.location.replace("/representative")
    if (res && res.hasLearner === false) return window.location.replace("/onboarding")
    return window.location.replace("/dashboard")
  }

  // Traduce el error del backend/contexto a un mensaje legible.
  const messageFor = (e) => {
    switch (e && e.code) {
      case error4.code:
        return t("errUserNotFound")
      case "noAccess":
        return t("noAccess")
      case "invalid-email":
        return t("otpErrInvalidEmail")
      case "wrong-code":
        return t("otpErrWrong")
      case "expired":
        return t("otpErrExpired")
      case "no-code":
        return t("otpErrNoCode")
      case "too-many-attempts":
      case "too-many-requests":
        return t("otpErrTooMany")
      default:
        return t("verifyData")
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    try {
      setLoading(true)
      const res = await signIn(email, password)
      setLoading(false)
      routeAfterAuth(res)
    } catch (e) {
      setLoading(false)
      setError(e.code)
      Toast.fire({ icon: "error", title: messageFor(e) })
    }
  }

  // ── OTP: enviar código ──
  const handleSendCode = async (e) => {
    if (e) e.preventDefault()
    const clean = otpEmail.trim().toLowerCase()
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(clean)) {
      Toast.fire({ icon: "error", title: t("otpErrInvalidEmail") })
      return
    }
    try {
      setSending(true)
      const data = await requestOtp(clean, language)
      setSending(false)
      setOtpStep("code")
      setCode(["", "", "", "", "", ""])
      setCooldown(data.cooldown || 45)
      Toast.fire({ icon: "success", title: t("otpSentToast") })
      setTimeout(() => cellRefs.current[0] && cellRefs.current[0].focus(), 50)
    } catch (err) {
      setSending(false)
      // Si hay cooldown, quizá ya exista un código vigente: deja escribirlo.
      if (err.code === "cooldown") {
        setOtpStep("code")
        setCooldown(err.retryAfter || 45)
        setTimeout(() => cellRefs.current[0] && cellRefs.current[0].focus(), 50)
        return
      }
      Toast.fire({ icon: "error", title: err.code === "too-many-requests" ? t("otpErrTooMany") : t("otpErrSend") })
    }
  }

  const submitCode = async (fullCode) => {
    try {
      setVerifying(true)
      const res = await verifyOtp(otpEmail.trim().toLowerCase(), fullCode)
      setVerifying(false)
      routeAfterAuth(res)
    } catch (err) {
      setVerifying(false)
      setCode(["", "", "", "", "", ""])
      setTimeout(() => cellRefs.current[0] && cellRefs.current[0].focus(), 50)
      Toast.fire({ icon: "error", title: messageFor(err) })
    }
  }

  // Manejo de la rejilla de 6 celdas: solo dígitos, auto-avance y auto-verificación.
  const setCell = (i, val) => {
    const next = [...code]
    next[i] = val
    setCode(next)
    if (val && i < 5) cellRefs.current[i + 1] && cellRefs.current[i + 1].focus()
    const full = next.join("")
    if (full.length === 6 && next.every((d) => d !== "")) submitCode(full)
  }

  const handleCellChange = (i, e) => {
    const digit = e.target.value.replace(/\D/g, "").slice(-1)
    setCell(i, digit)
  }

  const handleCellKeyDown = (i, e) => {
    if (e.key === "Backspace" && !code[i] && i > 0) {
      cellRefs.current[i - 1] && cellRefs.current[i - 1].focus()
    }
  }

  const handleCellPaste = (e) => {
    const pasted = (e.clipboardData.getData("text") || "").replace(/\D/g, "").slice(0, 6)
    if (!pasted) return
    e.preventDefault()
    const next = ["", "", "", "", "", ""]
    for (let k = 0; k < pasted.length; k++) next[k] = pasted[k]
    setCode(next)
    const focusIdx = Math.min(pasted.length, 5)
    cellRefs.current[focusIdx] && cellRefs.current[focusIdx].focus()
    if (pasted.length === 6) submitCode(pasted)
  }

  const switchMode = (m) => {
    setMode(m)
    setOtpStep("email")
    setCode(["", "", "", "", "", ""])
  }

  if (user === undefined) return <Loading />
  if (loading) return <Loading />

  return (
    <>
      <div className="login-content">
        <form onSubmit={mode === "password" ? handleSubmit : handleSendCode}>
          <Link to={"/"}>
            <img src="/assets/img/landing-images/MiABC-33.png" alt="MiABC Logo" className="auth-logo" />
          </Link>
          <h2 className="title">{t("welcome")}</h2>

          {/* Selector de método de acceso */}
          <div className="otp-seg" role="tablist">
            <button
              type="button"
              role="tab"
              aria-selected={mode === "password"}
              className={`otp-seg-btn ${mode === "password" ? "is-active" : ""}`}
              onClick={() => switchMode("password")}
            >
              {t("otpTabPassword")}
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={mode === "otp"}
              className={`otp-seg-btn ${mode === "otp" ? "is-active" : ""}`}
              onClick={() => switchMode("otp")}
            >
              {t("otpTabCode")}
            </button>
          </div>

          {mode === "password" && (
            <>
              <div id="input-div-login" className="input-div one">
                <div className="i">
                  <i className="fas fa-user"></i>
                </div>
                <Form.Control
                  type="email"
                  placeholder={t("email")}
                  name="email"
                  id="email"
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
              <div id="input-div-password" className="input-div pass">
                <div className="i">
                  <i className="fas fa-lock"></i>
                </div>
                <div className="relative w-100">
                  <Form.Control
                    type={toggleEye === false ? "password" : "text"}
                    placeholder={t("password")}
                    name="password"
                    id="password"
                    onChange={(e) => setPassword(e.target.value)}
                    className="input-div-password"
                  />
                  <div
                    onClick={handleToggleEye}
                    className="i absolute eye-position"
                    style={{ cursor: "pointer", right: "10px", top: "50%", transform: "translateY(-50%)", zIndex: 10 }}
                  >
                    {toggleEye === false ? <FaEye /> : <FaEyeSlash />}
                  </div>
                </div>
              </div>
              <Link to="/forgot-password" className="text-link-1">
                {t("forgotPassword")}
              </Link>
              <input type="submit" className="btn" value={t("loginButton")} />
            </>
          )}

          {mode === "otp" && otpStep === "email" && (
            <>
              <div className="input-div one">
                <div className="i">
                  <i className="fas fa-envelope"></i>
                </div>
                <Form.Control
                  type="email"
                  placeholder={t("email")}
                  name="otp-email"
                  id="otp-email"
                  value={otpEmail}
                  onChange={(e) => setOtpEmail(e.target.value)}
                  autoComplete="email"
                />
              </div>
              <button type="submit" className="btn" disabled={sending}>
                {sending ? t("otpSending") : t("otpSendCode")}
              </button>
            </>
          )}

          {mode === "otp" && otpStep === "code" && (
            <>
              <p className="otp-hint">
                {t("otpSentTo")} <strong>{otpEmail.trim().toLowerCase()}</strong>
              </p>
              <div className="otp-grid" onPaste={handleCellPaste}>
                {code.map((d, i) => (
                  <input
                    key={i}
                    ref={(el) => (cellRefs.current[i] = el)}
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={1}
                    className="otp-cell"
                    value={d}
                    disabled={verifying}
                    onChange={(e) => handleCellChange(i, e)}
                    onKeyDown={(e) => handleCellKeyDown(i, e)}
                    aria-label={`Dígito ${i + 1}`}
                  />
                ))}
              </div>
              <button
                type="button"
                className="btn"
                disabled={verifying || code.some((d) => d === "")}
                onClick={() => submitCode(code.join(""))}
              >
                {verifying ? t("otpVerifying") : t("otpVerify")}
              </button>
              <p className="otp-check-spam">{t("otpCheckSpam")}</p>
              <div className="otp-actions">
                {cooldown > 0 ? (
                  <span className="otp-resend is-disabled">
                    {t("otpResendIn")} 0:{String(cooldown).padStart(2, "0")}
                  </span>
                ) : (
                  <span className="otp-resend cursor-pointer" onClick={() => handleSendCode()}>
                    {t("otpResend")}
                  </span>
                )}
                <span className="otp-sep">·</span>
                <span className="otp-resend cursor-pointer" onClick={() => switchMode("otp")}>
                  {t("otpChangeEmail")}
                </span>
              </div>
            </>
          )}

          <Link to="/register/step1" className="text-link-1">
            {t("noAccount")} <strong>{t("register")}</strong>
          </Link>
          <p className="text-link-1 mt-2">
            {t("privacyText")}{" "}
            <Link to="/privacy" target="_blank">
              {t("privacyPolicy")}
            </Link>
          </p>
        </form>
      </div>
    </>
  )
}

export default LoginPage
